import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Dot, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import BaseIntegrationIcon from "@reusables/BaseIntegrationIcon";
import BaseMaterialCheckbox from "@reusables/BaseMaterialCheckbox";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import UIButton from "@reusables/UIButton";
import {
    useImportPowerOfficeCustomersMutation,
    useImportPowerOfficeSuppliersMutation,
} from "@redux/features/integrations/powerofficeApi";
import { isErrorWithMessage } from "@redux/api/query";
import { useAbility } from "@/casl.config";
import { isSystemIntegratedSelector } from "@redux/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { cn } from "@helpers/utils";
import {
    clearPowerOfficeRecordImportStatus,
    failPowerOfficeRecordImport,
    finishPowerOfficeRecordImport,
    markPowerOfficeRecordImportAsRead,
    PowerOfficeRecordImportEntity,
    PowerOfficeRecordImportError,
    selectPowerOfficeRecordImportActiveEntity,
    selectPowerOfficeRecordImportStatus,
    startPowerOfficeRecordImport,
} from "@redux/slices/powerOfficeRecordImportSlice";

interface PowerOfficeRecordSyncButtonProps {
    entity: PowerOfficeRecordImportEntity;
}

function parseImportError(error: unknown, fallbackMessage: string): PowerOfficeRecordImportError {
    if (isErrorWithMessage(error)) {
        return {
            code: error.code,
            message: error.message,
        };
    }

    if (error instanceof Error) {
        return {
            message: error.message,
        };
    }

    return {
        message: fallbackMessage,
    };
}

export default function PowerOfficeRecordSyncButton({ entity }: PowerOfficeRecordSyncButtonProps) {
    const { t, i18n } = useTranslation("", { keyPrefix: "imports.modules.poweroffice.recordSync" });

    const ability = useAbility();
    const dispatch = useAppDispatch();
    const companyId = useAppSelector((state) => state.auth.user?.company?.id);
    const isPowerOfficeIntegrated = useAppSelector(isSystemIntegratedSelector("poweroffice"));
    const activeEntity = useAppSelector(selectPowerOfficeRecordImportActiveEntity);
    const latestStatus = useAppSelector(selectPowerOfficeRecordImportStatus(entity));

    const [importCustomers] = useImportPowerOfficeCustomersMutation();
    const [importSuppliers] = useImportPowerOfficeSuppliersMutation();

    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [shouldClearStatusOnClose, setShouldClearStatusOnClose] = useState(false);

    const entityLabel = t(`entities.${entity}`) as string;
    const isAnyImportActive = !!activeEntity;
    const isCurrentImportActive = activeEntity === entity;
    const hasRowErrors = latestStatus?.status === "success" && latestStatus.result.errors > 0;
    const failures =
        latestStatus?.status === "success" ? latestStatus.result.failures ?? [] : undefined;
    const permissionModule = entity === "customers" ? "customer" : "supplier";
    const canImport = ability.can("create", permissionModule);

    useEffect(() => {
        if (!isStatusModalOpen) {
            setShouldClearStatusOnClose(false);
        }
    }, [isStatusModalOpen]);

    if (!isPowerOfficeIntegrated || !canImport || !companyId) {
        return null;
    }

    const handleImport = () => {
        if (isAnyImportActive) {
            return;
        }

        if (!companyId) {
            toast.error(t("toasts.error", { entity: entityLabel }));
            return;
        }

        dispatch(startPowerOfficeRecordImport(entity));

        const importRequest =
            entity === "customers" ? importCustomers().unwrap() : importSuppliers().unwrap();

        importRequest
            .then((result) => {
                dispatch(
                    finishPowerOfficeRecordImport({
                        entity,
                        companyId,
                        result,
                    })
                );

                if (result.errors > 0) {
                    toast.warn(t("toasts.warning", { entity: entityLabel, count: result.errors }));
                } else {
                    toast.success(t("toasts.success", { entity: entityLabel }));
                }
            })
            .catch((error) => {
                const requestError = parseImportError(
                    error,
                    t("toasts.error", { entity: entityLabel })
                );

                dispatch(
                    failPowerOfficeRecordImport({
                        entity,
                        companyId,
                        error: requestError,
                    })
                );

                toast.error(requestError.message, { autoClose: false });
            });
    };

    const handleStatusClick = () => {
        if (!latestStatus) {
            return;
        }

        dispatch(markPowerOfficeRecordImportAsRead(entity));
        setIsStatusModalOpen(true);
    };

    const handleStatusModalClose = () => {
        setIsStatusModalOpen(false);

        if (shouldClearStatusOnClose) {
            dispatch(clearPowerOfficeRecordImportStatus(entity));
        }
    };

    const completedAt =
        latestStatus &&
        new Intl.DateTimeFormat(i18n.language, {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(new Date(latestStatus.completedAt));

    return (
        <>
            <div className="flex min-h-9 items-center gap-2" aria-live="polite">
                {(latestStatus || isCurrentImportActive) && (
                    <StatusIcon
                        entityLabel={entityLabel}
                        hasRowErrors={hasRowErrors}
                        isActive={isCurrentImportActive}
                        isUnread={!!latestStatus?.unread}
                        status={latestStatus?.status}
                        onClick={handleStatusClick}
                    />
                )}

                <UIButton
                    type="button"
                    variant="secondary"
                    size="md"
                    loading={isCurrentImportActive}
                    disabled={isAnyImportActive}
                    aria-label={t("aria.import", { entity: entityLabel })}
                    onClick={handleImport}
                >
                    <BaseIntegrationIcon integration="poweroffice" />
                    <span>{t("buttons.import")}</span>
                </UIButton>
            </div>

            <BaseModal
                isOpen={isStatusModalOpen}
                onClose={handleStatusModalClose}
                width={768}
                padding="40px"
                position="center"
                useCloseIcon
                closeIconClass="top-[24px] right-[32px]"
            >
                {latestStatus && (
                    <div className="space-y-[24px] text-accent">
                        <div className="flex items-start gap-3 pr-8">
                            <div className="mt-1">
                                <ModalStatusIcon
                                    status={latestStatus.status}
                                    hasRowErrors={hasRowErrors}
                                />
                            </div>
                            <div>
                                <div className="text-xl font-semibold">
                                    {latestStatus.status === "error" &&
                                        t("modal.errorHeading", { entity: entityLabel })}
                                    {latestStatus.status === "success" &&
                                        hasRowErrors &&
                                        t("modal.warningHeading", { entity: entityLabel })}
                                    {latestStatus.status === "success" &&
                                        !hasRowErrors &&
                                        t("modal.successHeading", { entity: entityLabel })}
                                </div>
                                {completedAt && (
                                    <div className="mt-1 text-sm text-secondary-gray">
                                        {t("modal.completedAt", { time: completedAt })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {latestStatus.status === "success" ? (
                            <div className="space-y-[24px]">
                                <div>
                                    <div className="mb-2 text-sm font-medium text-secondary-gray">
                                        {t("modal.summary")}
                                    </div>
                                    <div className="divide-y divide-[#D9DAFD]">
                                        <ResultMetric
                                            label={t("modal.metrics.imported")}
                                            value={latestStatus.result.imported}
                                        />
                                        <ResultMetric
                                            label={t("modal.metrics.updated")}
                                            value={latestStatus.result.updated}
                                        />
                                        <ResultMetric
                                            label={t("modal.metrics.skipped")}
                                            value={latestStatus.result.skipped}
                                        />
                                        <ResultMetric
                                            label={t("modal.metrics.errors")}
                                            value={latestStatus.result.errors}
                                            isDanger={latestStatus.result.errors > 0}
                                        />
                                    </div>
                                </div>

                                {!!failures?.length && (
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium text-secondary-gray">
                                            {t("modal.failuresHeading")}
                                        </div>
                                        <div className="space-y-3">
                                            {failures.map((failure) => (
                                                <div
                                                    key={failure.power_office_id}
                                                    className="rounded-md border border-[#E25C5C]/20 bg-[#E25C5C]/5 px-4 py-3"
                                                >
                                                    <div className="space-x-1 font-medium text-accent text-sm">
                                                        <span>#{failure.number}</span>
                                                        <Dot size={16} className="text-accent" />
                                                        <span>{failure.name}</span>
                                                    </div>
                                                    <div className="mt-1 text-sm text-secondary-gray">
                                                        {failure.error}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="rounded-md border border-[#E25C5C]/30 bg-[#E25C5C]/5 p-4">
                                <div className="text-sm text-secondary-gray">
                                    {t("modal.errorMessage")}
                                </div>
                                <div className="mt-2 font-medium">{latestStatus.error.message}</div>
                                {latestStatus.error.code && (
                                    <div className="mt-2 text-sm text-secondary-gray">
                                        {t("modal.errorCode", { code: latestStatus.error.code })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-4">
                            <label className="flex cursor-pointer items-center gap-3 text-sm text-accent">
                                <BaseMaterialCheckbox
                                    checked={shouldClearStatusOnClose}
                                    onChange={(_, checked) => setShouldClearStatusOnClose(checked)}
                                />
                                <span>{t("modal.clearOnClose")}</span>
                            </label>

                            <BaseButton
                                type="button"
                                text={t("modal.close")}
                                size="md"
                                primaryOutlined
                                onClick={handleStatusModalClose}
                            />
                        </div>
                    </div>
                )}
            </BaseModal>
        </>
    );
}

interface StatusIconProps {
    entityLabel: string;
    hasRowErrors: boolean;
    isActive: boolean;
    isUnread: boolean;
    status?: "success" | "error";
    onClick: () => void;
}

function StatusIcon({
    entityLabel,
    hasRowErrors,
    isActive,
    isUnread,
    status,
    onClick,
}: StatusIconProps) {
    const { t } = useTranslation("", { keyPrefix: "imports.modules.poweroffice.recordSync" });

    const icon = (() => {
        if (isActive) {
            return (
                <Loader2
                    size={16}
                    className="animate-spin text-[#7556FA] motion-reduce:animate-none"
                />
            );
        }

        if (status === "error") {
            return <AlertCircle size={16} className="text-[#E25C5C]" />;
        }

        if (hasRowErrors) {
            return <AlertCircle size={16} className="text-[#D97706]" />;
        }

        return <CheckCircle2 size={16} className="text-[#A6DD4C]" />;
    })();

    if (isActive) {
        return (
            <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#D9DAFD] bg-white">
                {icon}
            </div>
        );
    }

    return (
        <button
            type="button"
            className={cn(
                "relative flex h-9 w-9 items-center justify-center rounded-md border border-[#D9DAFD] bg-white transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-[#7556FA] hover:bg-[#F7F5FF] hover:shadow-[0_2px_8px_rgba(60,55,105,0.08)] active:translate-y-px active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7556FA]/30 motion-reduce:transition-none motion-reduce:active:translate-y-0 motion-reduce:active:scale-100",
                isUnread && "border-[#7556FA]"
            )}
            aria-label={t("aria.status", { entity: entityLabel })}
            onClick={onClick}
        >
            {icon}
            {isUnread && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#7556FA] motion-safe:animate-pulse" />
            )}
        </button>
    );
}

interface ModalStatusIconProps {
    status: "success" | "error";
    hasRowErrors: boolean;
}

function ModalStatusIcon({ status, hasRowErrors }: ModalStatusIconProps) {
    if (status === "error") {
        return <AlertCircle size={28} className="text-[#E25C5C]" />;
    }

    if (hasRowErrors) {
        return <AlertCircle size={28} className="text-[#D97706]" />;
    }

    return <CheckCircle2 size={28} className="text-[#A6DD4C]" />;
}

interface ResultMetricProps {
    label: string;
    value: number;
    isDanger?: boolean;
}

function ResultMetric({ label, value, isDanger }: ResultMetricProps) {
    return (
        <li className={`flex items-center justify-between gap-2`}>
            {label}: {value}
        </li>
    );
}
